import type { Dayjs } from "dayjs";
import { type AssetPosition, timeSeriesDataProjectionEnum } from "kubb";
import type React from "react";
import { useGetAssetTimeSeriesData } from "../../../../hooks/getAssetTimeSeriesData.tsx";
import {
  type ColumnDefinitionWithData,
  type ColumnMap,
  mapColumns,
} from "../../../../model/deviceTimeSeries";
import type { Asset } from "../../../../utils/object.tsx";
import type { AssetTimeSeriesRequestParams } from "./AssetDataSelectionForm";
import type { DeviceTimeSeriesDisplayComponentProps } from "./model";

export interface AssetTimeSeriesDataElementProps<
  T = string | undefined,
  D extends ColumnDefinitionWithData<T> = ColumnDefinitionWithData<T>,
> {
  mappedTimeSeriesColumns: ColumnMap<T, D>;
  times: Dayjs[];
  params: AssetTimeSeriesRequestParams;
  loading: boolean;
  error?: Error;
}

interface Props {
  device: Asset | AssetPosition;
  params: AssetTimeSeriesRequestParams;
  children: (props: AssetTimeSeriesDataElementProps) => React.ReactNode;
}

export default function AssetTimeSeriesDataGetter(
  props: Props,
): React.ReactElement {
  const { device, params } = props;
  const { start_datetime, end_datetime, projection } = params;

  const { data, loading, error } = useGetAssetTimeSeriesData(
    device,
    start_datetime,
    end_datetime,
    projection,
  );

  const displayProps = (): DeviceTimeSeriesDisplayComponentProps => {
    if (data) {
      return {
        mappedTimeSeriesColumns: mapColumns(
          data,
          projection === timeSeriesDataProjectionEnum.raw,
        ),
        times: data.times as Dayjs[],
        params: params,
        loading: loading,
        error: error ? error : undefined,
      };
    } else {
      return {
        mappedTimeSeriesColumns: {},
        times: [],
        params: params,
        loading: loading,
        error: error ? error : undefined,
      };
    }
  };

  return <>{props.children(displayProps())}</>;
}
